// ============================================================================
// Delhivery multistop route map — an embedded Google Map of the full pickup→…→drop route for the
// multistop modal, drawn along the ACTUAL road network (not straight lines). Given the ordered stops
// (pickups in route order, the customer drop last):
//   • We call the modern Routes API (routes.googleapis.com/directions/v2:computeRoutes) directly from
//     the browser with the quote's API key — same X-Goog-Api-Key pattern the procurement map-picker
//     uses for Places (New). origin = first stop, destination = last, the rest as `intermediates`,
//     TRAFFIC_AWARE for live traffic. (The JS DirectionsService can't be used — it only talks to the
//     legacy Directions API, which new GCP projects can no longer enable.)
//   • The response's encoded polyline is decoded locally (no geometry library needed) and drawn with a
//     PolylineF; each response leg gives the real road distance + traffic duration for stop i→i+1.
//   • Click a marker → an InfoWindowF with the stop name, exact lat/lng, and the road distance + ETA
//     to the NEXT stop. The side panel lists total route distance/ETA then every leg.
// Loads the map via @react-google-maps/api; the key comes from the quote response. Requires the Routes
// API (and Maps JavaScript API) enabled on that key.
// ============================================================================
import {
  GoogleMap,
  InfoWindowF,
  MarkerF,
  PolylineF,
  useLoadScript,
} from "@react-google-maps/api";
import { Spin, Tag, Typography } from "antd";
import React from "react";

const { Text } = Typography;

// Matches the app's other loader (src/components/misc/map-picker) so the useLoadScript singleton
// isn't re-invoked with a different `libraries` set. Module-level for a stable reference.
const LIBRARIES: "places"[] = ["places"];

const ROUTES_ENDPOINT =
  "https://routes.googleapis.com/directions/v2:computeRoutes";

export interface MultistopMapStop {
  id: string;
  title: string; // e.g. "Pickup · MD…" / "Stop 2 · MD…" / "Drop · Home"
  subtitle?: string; // vendor / address line
  isDrop?: boolean;
  position: { lat: number; lng: number };
}

// Live road metrics for one leg (stop i → stop i+1), pulled from the Routes response.
interface LegMetric {
  distanceText: string;
  durationText: string;
  inTraffic: boolean;
}

const ROUTE_COLOR = "#24AAB7";

const fmtLatLng = (p: { lat: number; lng: number }) =>
  `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`;

const fmtKm = (meters: number) =>
  meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`;

const fmtMins = (seconds: number) => {
  const mins = Math.round(seconds / 60);
  if (mins < 1) return "< 1 min";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"}`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
};

// Routes API returns durations as protobuf strings like "845s".
const parseSeconds = (v?: string | number | null): number => {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const n = parseFloat(String(v).replace(/s$/, ""));
  return Number.isFinite(n) ? n : 0;
};

// Decode a Google encoded polyline (precision 5) to lat/lng points — the standard algorithm, so we
// don't need to pull in the Maps "geometry" library (which would fork the shared loader's `libraries`).
const decodePolyline = (encoded: string): Array<{ lat: number; lng: number }> => {
  const points: Array<{ lat: number; lng: number }> = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < encoded.length) {
    let b: number;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;
    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
};

const DelhiveryMultistopMap: React.FC<{
  apiKey: string;
  stops: MultistopMapStop[];
  height?: number;
}> = ({ apiKey, stops, height = 320 }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
  });

  const mapRef = React.useRef<google.maps.Map | null>(null);
  const [activeIdx, setActiveIdx] = React.useState<number | null>(null);
  const [path, setPath] = React.useState<Array<{ lat: number; lng: number }>>([]);
  const [legs, setLegs] = React.useState<LegMetric[]>([]);
  const [totals, setTotals] = React.useState<{
    distanceText: string;
    durationText: string;
    inTraffic: boolean;
  } | null>(null);
  const [routeError, setRouteError] = React.useState<string | null>(null);
  const [routing, setRouting] = React.useState(false);

  // Re-route + re-fit whenever the ordered set of coordinates changes.
  const stopsKey = React.useMemo(
    () => stops.map((s) => `${s.position.lat},${s.position.lng}`).join("|"),
    [stops]
  );

  // Marker display positions: when several stops share the exact same coordinate (e.g. two POs from
  // the same vendor pickup), stacking their pins renders awkwardly and only the top one is clickable.
  // Fan same-point markers out in a tiny circle (~9 m) so each is visible and tappable. This only
  // affects where the PIN is drawn — the real lat/lng (info window) and the road path are unchanged.
  const markerPositions = React.useMemo(() => {
    const key = (p: { lat: number; lng: number }) =>
      `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;
    const counts = new Map<string, number>();
    stops.forEach((s) => counts.set(key(s.position), (counts.get(key(s.position)) ?? 0) + 1));
    const seen = new Map<string, number>();
    const R = 0.00008; // ~9 m in latitude degrees
    return stops.map((s) => {
      const k = key(s.position);
      const total = counts.get(k) ?? 1;
      if (total <= 1) return s.position;
      const i = seen.get(k) ?? 0;
      seen.set(k, i + 1);
      const angle = (2 * Math.PI * i) / total;
      return {
        lat: s.position.lat + R * Math.cos(angle),
        lng: s.position.lng + R * Math.sin(angle),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopsKey]);

  const fitToStops = React.useCallback(() => {
    const map = mapRef.current;
    if (!map || stops.length === 0) return;
    if (stops.length === 1) {
      map.setCenter(stops[0].position);
      map.setZoom(14);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    stops.forEach((s) => bounds.extend(s.position));
    map.fitBounds(bounds, 48);
  }, [stops]);

  // Reset the open info window if the route changed under it.
  React.useEffect(() => setActiveIdx(null), [stopsKey]);

  // One traffic-aware Routes request over the whole ordered route. Legs come back in stop order, so
  // legs[i] is the real road path metric from stop i → stop i+1.
  React.useEffect(() => {
    if (!isLoaded) return;
    if (stops.length < 2) {
      setPath([]);
      setLegs([]);
      setTotals(null);
      setRouteError(null);
      fitToStops();
      return;
    }
    let cancelled = false;
    setRouting(true);
    setRouteError(null);

    const toWaypoint = (s: MultistopMapStop) => ({
      location: { latLng: { latitude: s.position.lat, longitude: s.position.lng } },
    });

    fetch(ROUTES_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": apiKey,
        // Only ask for what we draw/read — Routes bills by the fields requested.
        "X-Goog-FieldMask": [
          "routes.polyline.encodedPolyline",
          "routes.distanceMeters",
          "routes.duration",
          "routes.legs.distanceMeters",
          "routes.legs.duration",
          "routes.legs.staticDuration",
        ].join(","),
      },
      body: JSON.stringify({
        origin: toWaypoint(stops[0]),
        destination: toWaypoint(stops[stops.length - 1]),
        intermediates: stops.slice(1, -1).map(toWaypoint),
        travelMode: "DRIVE",
        routingPreference: "TRAFFIC_AWARE", // live-traffic duration
        polylineQuality: "HIGH_QUALITY",
      }),
    })
      .then(async (resp) => {
        const data = await resp.json().catch(() => null);
        if (cancelled) return;
        setRouting(false);
        if (!resp.ok) {
          setPath([]);
          setLegs([]);
          setTotals(null);
          setRouteError(
            data?.error?.message || `Couldn't fetch the driving route (${resp.status}).`
          );
          fitToStops();
          return;
        }
        const route = data?.routes?.[0];
        if (!route) {
          setPath([]);
          setLegs([]);
          setTotals(null);
          setRouteError("No drivable route connects these stops.");
          fitToStops();
          return;
        }
        const encoded: string | undefined = route.polyline?.encodedPolyline;
        setPath(encoded ? decodePolyline(encoded) : []);
        const routeLegs: any[] = route.legs ?? [];
        const next: LegMetric[] = routeLegs.map((leg) => {
          const trafficSecs = parseSeconds(leg.duration);
          const staticSecs = parseSeconds(leg.staticDuration);
          return {
            distanceText: fmtKm(leg.distanceMeters ?? 0),
            durationText: fmtMins(trafficSecs || staticSecs),
            inTraffic: trafficSecs > staticSecs + 30, // meaningfully slower than free-flow
          };
        });
        setLegs(next);
        const totalTraffic = parseSeconds(route.duration);
        setTotals({
          distanceText: fmtKm(route.distanceMeters ?? 0),
          durationText: fmtMins(totalTraffic),
          inTraffic: next.some((l) => l.inTraffic),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setRouting(false);
        setPath([]);
        setLegs([]);
        setTotals(null);
        setRouteError("Couldn't reach the routing service.");
        fitToStops();
      });

    return () => {
      cancelled = true;
    };
  }, [isLoaded, stopsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fit to the road path once it's drawn (falls back to fitting the stops).
  React.useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    if (path.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      path.forEach((p) => bounds.extend(p));
      mapRef.current.fitBounds(bounds, 48);
    } else {
      fitToStops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, isLoaded]);

  // "4.2 km · 12 mins in traffic" for the leg out of stop i, or null for the last stop / no data.
  const legText = (i: number): string | null => {
    const m = legs[i];
    if (!m) return null;
    return `${m.distanceText} · ${m.durationText}${m.inTraffic ? " in traffic" : ""}`;
  };

  if (loadError)
    return (
      <div style={{ padding: 12 }}>
        <Text type="danger">Couldn't load Google Maps.</Text>
      </div>
    );

  if (!isLoaded)
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin />
      </div>
    );

  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
      {/* The map. */}
      <div
        style={{
          flex: "1 1 380px",
          minWidth: 300,
          height,
          borderRadius: 10,
          overflow: "hidden",
          border: "1px solid #f0f0f0",
        }}
      >
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
          onLoad={(map) => {
            mapRef.current = map;
            fitToStops();
          }}
          onUnmount={() => {
            mapRef.current = null;
          }}
        >
          {/* The real road path decoded from the Routes response. */}
          {path.length > 1 && (
            <PolylineF
              path={path}
              options={{
                strokeColor: ROUTE_COLOR,
                strokeOpacity: 0.9,
                strokeWeight: 5,
              }}
            />
          )}
          {stops.map((s, i) => (
            <MarkerF
              key={s.id}
              position={markerPositions[i]}
              label={{
                text: String(i + 1),
                color: "#ffffff",
                fontWeight: "bold",
                fontSize: "12px",
              }}
              onClick={() => setActiveIdx(i)}
            />
          ))}
          {activeIdx != null && stops[activeIdx] && (
            <InfoWindowF
              position={markerPositions[activeIdx]}
              onCloseClick={() => setActiveIdx(null)}
            >
              <div style={{ maxWidth: 220 }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>
                  {stops[activeIdx].title}
                </div>
                {stops[activeIdx].subtitle && (
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
                    {stops[activeIdx].subtitle}
                  </div>
                )}
                <div style={{ fontSize: 12, color: "#595959" }}>
                  {fmtLatLng(stops[activeIdx].position)}
                </div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  {activeIdx < stops.length - 1 ? (
                    legText(activeIdx) ? (
                      <span>
                        To {stops[activeIdx + 1].title}:{" "}
                        <b>{legText(activeIdx)}</b>
                      </span>
                    ) : (
                      <span style={{ color: "#8c8c8c" }}>
                        {routing ? "Measuring route…" : "—"}
                      </span>
                    )
                  ) : (
                    <span style={{ color: "#8c8c8c" }}>Final drop</span>
                  )}
                </div>
              </div>
            </InfoWindowF>
          )}
        </GoogleMap>
      </div>

      {/* Side panel — total route metrics, then every stop with its road distance/ETA to the next hop. */}
      <div
        style={{
          flex: "1 1 240px",
          minWidth: 220,
          maxHeight: height,
          overflowY: "auto",
          border: "1px solid #f0f0f0",
          borderRadius: 10,
          padding: 10,
        }}
      >
        {/* Total route summary. */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: "#8c8c8c" }}>Total route</div>
          {routing && !totals ? (
            <Spin size="small" />
          ) : totals ? (
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {totals.distanceText} · {totals.durationText}
              {totals.inTraffic ? " in traffic" : ""}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#8c8c8c" }}>—</div>
          )}
        </div>

        {routeError && (
          <Text type="warning" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
            {routeError}
          </Text>
        )}

        {stops.map((s, i) => {
          const active = i === activeIdx;
          return (
            <div
              key={s.id}
              role="button"
              onClick={() => setActiveIdx(i)}
              style={{
                display: "flex",
                gap: 10,
                padding: "8px 8px",
                borderRadius: 8,
                cursor: "pointer",
                background: active ? "#e6fbfc" : "transparent",
              }}
            >
              {/* Numbered badge, matching the map marker. */}
              <div
                style={{
                  flexShrink: 0,
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: s.isDrop ? "#1677ff" : ROUTE_COLOR,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {i + 1}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {s.title}
                  {s.isDrop && (
                    <Tag color="blue" style={{ marginLeft: 6 }}>
                      Drop
                    </Tag>
                  )}
                </div>
                {s.subtitle && (
                  <div style={{ fontSize: 11, color: "#8c8c8c" }}>{s.subtitle}</div>
                )}
                <div style={{ fontSize: 11, color: "#595959" }}>
                  {fmtLatLng(s.position)}
                </div>
                {/* Road distance/ETA to the next stop (or the terminal marker). */}
                {i < stops.length - 1 ? (
                  <div style={{ fontSize: 12, marginTop: 2 }}>
                    ↓{" "}
                    {legText(i) ? (
                      <span style={{ color: ROUTE_COLOR }}>{legText(i)}</span>
                    ) : (
                      <span style={{ color: "#8c8c8c" }}>
                        {routing ? "Measuring…" : "—"}
                      </span>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, marginTop: 2, color: "#8c8c8c" }}>
                    Final drop
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DelhiveryMultistopMap;
